import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Globe, Code2, Loader2, Send } from "lucide-react";
import { ShareWidgetDialog } from "@/components/mc/ShareWidgetDialog";

const PLAN_LABEL = { included: "Included", off: "Not in plan", override: "Test override" };
const PLAN_STYLE = { included: "border-primary text-primary", off: "border-border text-muted-foreground", override: "border-amber-400 text-amber-700 bg-amber-50" };
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WIDGETS, hostedPath, snippet } from "@/lib/widgets";

function WidgetCard({ w, shops, sampleSlug, onCreateSample, creating }) {
  const [slug, setSlug] = useState(sampleSlug || "");
  const [showCode, setShowCode] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [plan, setPlan] = useState(null);
  useEffect(() => { if (!slug && sampleSlug) setSlug(sampleSlug); }, [sampleSlug, slug]);
  useEffect(() => {
    if (!slug) return;
    opApi.get("/admin/widgets/status", { params: { slug } }).then(({ data }) => setPlan(data.widgets?.[w.key])).catch(() => setPlan(null));
  }, [slug, w.key]);
  const code = slug ? snippet(w, slug) : "";
  const copy = () => navigator.clipboard.writeText(code).then(() => toast.success("Embed code copied"));
  return (
    <div className="border-r border-b border-border p-5 flex flex-col gap-3" data-testid={`widget-card-${w.key}`}>
      <div>
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display font-bold text-lg">{w.label}</h3>
          {w.product === "morrcart" && plan && (
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${PLAN_STYLE[plan]}`} data-testid={`widget-plan-${w.key}`}>{PLAN_LABEL[plan]}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{w.desc}</p>
      </div>
      <label className="text-xs text-muted-foreground">Shop
        <select className="mt-0.5 w-full border border-input bg-background h-8 px-2 text-sm" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid={`widget-shop-${w.key}`}>
          {!sampleSlug && <option value="">Choose a shop…</option>}
          {shops.map((s) => <option key={s.slug} value={s.slug}>{s.sandbox ? `Sample · ${s.name.replace(/^Sandbox · /, "")}` : s.name}</option>)}
        </select>
      </label>
      {!sampleSlug && (
        <Button size="sm" variant="outline" className="rounded-none" disabled={creating} onClick={() => onCreateSample(w.sample)} data-testid={`widget-create-sample-${w.key}`}>
          {creating && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Create sample shop
        </Button>
      )}
      <div className="flex flex-wrap gap-2 mt-auto">
        <Button asChild size="sm" className="rounded-none gap-1.5" disabled={!slug}>
          <a href={slug ? hostedPath(w, slug) : undefined} target="_blank" rel="noreferrer" data-testid={`widget-open-${w.key}`}><ExternalLink className="h-3.5 w-3.5" /> Open hosted page</a>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-none gap-1.5" disabled={!slug}>
          <a href={slug ? `/w/${slug}/${w.key}?site=1` : undefined} target="_blank" rel="noreferrer" data-testid={`widget-site-${w.key}`}><Globe className="h-3.5 w-3.5" /> See on sample website</a>
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none gap-1.5" disabled={!slug} onClick={() => setShowCode((v) => !v)} data-testid={`widget-code-toggle-${w.key}`}><Code2 className="h-3.5 w-3.5" /> Embed code</Button>
        <Button size="sm" variant="ghost" className="rounded-none gap-1.5" disabled={!slug} onClick={() => setSharing(true)} data-testid={`widget-share-${w.key}`}><Send className="h-3.5 w-3.5" /> Send to client</Button>
      </div>
      {slug && <ShareWidgetDialog w={w} slug={slug} open={sharing} onOpenChange={setSharing} />}
      {showCode && slug && (
        <div className="relative" data-testid={`widget-code-${w.key}`}>
          <pre className="bg-foreground text-background text-[11px] p-3 pr-10 whitespace-pre-wrap break-all">{code}</pre>
          <button type="button" className="absolute top-2 right-2 text-background/80 hover:text-background" onClick={copy} data-testid={`widget-copy-${w.key}`}><Copy className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

export function WidgetsPanel({ tenants }) {
  const [sandboxes, setSandboxes] = useState({});
  const [creating, setCreating] = useState(null);
  const load = () => opApi.get("/admin/verticals").then(({ data }) => {
    setSandboxes(Object.fromEntries(data.verticals.filter((v) => v.sandbox).map((v) => [v.key, v.sandbox])));
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const shops = useMemo(() => [
    ...Object.values(sandboxes).map((s) => ({ slug: s.slug, name: s.name, sandbox: true })),
    ...(tenants || []).filter((t) => t.slug).map((t) => ({ slug: t.slug, name: t.name })),
  ], [sandboxes, tenants]);
  const createSample = async (key) => {
    setCreating(key);
    try { await opApi.post(`/admin/verticals/${key}/sandbox`); await load(); toast.success("Sample shop ready"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(null); }
  };
  const group = (product, title) => (
    <div className="mb-8">
      <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
        {WIDGETS.filter((w) => w.product === product).map((w) => (
          <WidgetCard key={w.key} w={w} shops={shops} sampleSlug={sandboxes[w.sample]?.slug} onCreateSample={createSample} creating={creating === w.sample} />
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="widgets-panel">
      <h2 className="font-display font-bold text-xl">Widgets</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-2xl">Open and test any widget on its own, without setting up a shop. Each one uses a sample shop by default; switch shops to test with real settings. Anything you do here is saved only in that shop. To refresh sample data, use Reset sample data on the Verticals tab.</p>
      {group("morrcart", "MorrCart")}
      {group("morrconnect", "MorrConnect")}
    </div>
  );
}
